import { useCallback, useEffect, useState } from "react";
import { useAuth } from "../../auth/AuthContext";

export function LockoutsTab() {
  const { apiFetch } = useAuth();
  const [data, setData] = useState({ items: [], max_attempts: 0, lockout_minutes: 0 });
  const [loading, setLoading] = useState(true);
  const [clearing, setClearing] = useState(null);
  const [manualEmail, setManualEmail] = useState("");
  const [msg, setMsg] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await apiFetch(`/api/auth/lockouts`);
      setData(d);
    } catch (e) {
      setMsg(`Error: ${e.message}`);
    } finally {
      setLoading(false);
    }
  }, [apiFetch]);

  useEffect(() => { load(); }, [load]);

  const clearOne = async (email) => {
    setClearing(email);
    setMsg(null);
    try {
      const d = await apiFetch(`/api/auth/lockouts/clear`, {
        method: "POST",
        body: JSON.stringify({ email }),
      });
      setMsg(d.cleared ? `Cleared lockout for ${email}` : `No lockout found for ${email}`);
      await load();
      if (manualEmail.trim().toLowerCase() === email.toLowerCase()) setManualEmail("");
    } catch (e) {
      setMsg(`Error: ${e.message}`);
    } finally {
      setClearing(null);
    }
  };

  return (
    <div className="mt-8">
      <div className="mb-6 flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] tracking-[0.3em] font-bold text-[var(--srs-ink-faint)] mb-2">// LOCKOUTS</div>
          <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-[var(--srs-ink)]">
            Brute-force login lockouts
          </h2>
          <p className="mt-2 text-sm text-[var(--srs-ink-dim)] max-w-2xl">
            Accounts are locked for <strong>{data.lockout_minutes} min</strong> after <strong>{data.max_attempts}</strong> failed login attempts.
            Clear an account here to let the user retry immediately.
          </p>
        </div>
        <button onClick={load} disabled={loading}
          className="px-3 py-2 text-[10px] tracking-[0.2em] font-bold uppercase border border-[var(--srs-line)] text-[var(--srs-ink-dim)] hover:text-[var(--srs-ink)] hover:border-[var(--srs-sun)] transition-colors disabled:opacity-50"
          data-testid="lockouts-refresh-btn">
          {loading ? "Loading…" : "Refresh"}
        </button>
      </div>

      {/* Manual clear by email (for users whose attempt records weren't created yet under the new threshold) */}
      <div className="mb-6 p-5 border border-[var(--srs-line)] bg-[var(--srs-surface)] rounded-lg">
        <div className="text-[10px] tracking-[0.25em] font-bold text-[var(--srs-ink-faint)] mb-3">CLEAR BY EMAIL</div>
        <div className="flex gap-2 flex-wrap">
          <input
            type="email"
            value={manualEmail}
            onChange={(e) => setManualEmail(e.target.value)}
            placeholder="user@example.com"
            className="flex-1 min-w-[220px] bg-[var(--srs-surface-2)] border border-[var(--srs-line)] outline-none px-4 py-2.5 text-sm text-[var(--srs-ink)] placeholder:text-[var(--srs-ink-faint)] focus:border-[var(--srs-sun)] transition-colors rounded"
            data-testid="lockouts-manual-email"
          />
          <button
            onClick={() => manualEmail.trim() && clearOne(manualEmail.trim().toLowerCase())}
            disabled={!manualEmail.trim() || !!clearing}
            className="px-5 py-2.5 text-[11px] tracking-[0.2em] font-bold uppercase bg-[var(--srs-sun)] text-white hover:bg-[var(--srs-sun-deep)] transition-colors disabled:opacity-50 rounded"
            data-testid="lockouts-manual-clear-btn">
            {clearing === manualEmail.trim().toLowerCase() ? "Clearing…" : "Clear"}
          </button>
        </div>
        {msg && <div className="mt-3 text-xs text-[var(--srs-ink-dim)]" data-testid="lockouts-msg">{msg}</div>}
      </div>

      {/* Active lockouts list */}
      {loading ? (
        <div className="p-12 text-center text-sm text-[var(--srs-ink-dim)]">Loading…</div>
      ) : data.items.length === 0 ? (
        <div className="p-12 text-center border border-dashed border-[var(--srs-line)] rounded-lg">
          <div className="text-[var(--srs-good)] mb-2">✓</div>
          <div className="text-sm text-[var(--srs-ink-dim)]">No failed login attempts on record.</div>
        </div>
      ) : (
        <div className="border border-[var(--srs-line)] rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[var(--srs-surface)] border-b border-[var(--srs-line)]">
                <th className="text-left px-4 py-3 text-[10px] tracking-[0.25em] font-bold text-[var(--srs-ink-faint)]">EMAIL</th>
                <th className="text-left px-4 py-3 text-[10px] tracking-[0.25em] font-bold text-[var(--srs-ink-faint)]">FAILED ATTEMPTS</th>
                <th className="text-left px-4 py-3 text-[10px] tracking-[0.25em] font-bold text-[var(--srs-ink-faint)]">STATUS</th>
                <th className="text-left px-4 py-3 text-[10px] tracking-[0.25em] font-bold text-[var(--srs-ink-faint)]">LAST ATTEMPT</th>
                <th className="text-right px-4 py-3 text-[10px] tracking-[0.25em] font-bold text-[var(--srs-ink-faint)]">ACTION</th>
              </tr>
            </thead>
            <tbody data-testid="lockouts-table">
              {data.items.map((row) => (
                <tr key={row.email} className="border-b border-[var(--srs-line-soft)] last:border-0">
                  <td className="px-4 py-3 font-mono text-xs text-[var(--srs-ink)]" data-testid={`lockout-email-${row.email}`}>{row.email}</td>
                  <td className="px-4 py-3 text-[var(--srs-ink)]">{row.count}</td>
                  <td className="px-4 py-3">
                    {row.is_locked ? (
                      <span className="px-2 py-0.5 text-[10px] tracking-[0.15em] font-bold uppercase rounded-full bg-[#FDECEC] text-[#B71C1C]">Locked</span>
                    ) : (
                      <span className="px-2 py-0.5 text-[10px] tracking-[0.15em] font-bold uppercase rounded-full bg-[var(--srs-line-soft)] text-[var(--srs-ink-dim)]">Active</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-xs text-[var(--srs-ink-dim)]">
                    {row.last_at ? new Date(row.last_at).toLocaleString() : "—"}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={() => clearOne(row.email)}
                      disabled={clearing === row.email}
                      className="px-3 py-1.5 text-[10px] tracking-[0.2em] font-bold uppercase border border-[var(--srs-line)] text-[var(--srs-ink-dim)] hover:text-[var(--srs-sun)] hover:border-[var(--srs-sun)] transition-colors disabled:opacity-50 rounded"
                      data-testid={`lockout-clear-${row.email}`}>
                      {clearing === row.email ? "Clearing…" : "Clear"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}


// ---------------------------------------------------------------------------
// Support Tab — admin view of user-submitted tickets. Threaded conversation,
// reply inline, change status. Separate from /reports (per-merchant problems).
// ---------------------------------------------------------------------------
